"use client";

import { useEffect, useState } from "react";
import Header from "./components/header";
import Main from "./components/main";
import Sidebar from "./components/sidebar";
import type { SidebarLabel } from "./components/sidebar";
import type { UserGuideDocKey } from "./constants/userGuideDocs";
import { useAuth } from "./hooks/useAuth";
import TeacherLogin from "./components/teacherLogin";
import MainLogin from "./components/mainLogin";
import { IoClose } from "react-icons/io5";
import ConfirmPopUp from "./components/popup/confirmPopUp";
import UserGuidePdfViewer from "./components/userGuide/UserGuidePdfViewer";
import {
    GameEditorLeaveGuardProvider,
    useGameEditorLeaveGuard,
} from "./components/games/shared/GameEditorLeaveGuard";
import { useTranslations } from "next-intl";

function HomeContent() {
  const [selectedLabel, setSelectedLabel] = useState<SidebarLabel>(
    "คลังบทเรียนต้นแบบ"
  );
  const [selectedUserGuideDoc, setSelectedUserGuideDoc] =
    useState<UserGuideDocKey | null>(null);
  const [showTeacherLoginScreen, setShowTeacherLoginScreen] = useState(false);
  const [hasLocalStorageUser, setHasLocalStorageUser] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [pendingSidebarLabel, setPendingSidebarLabel] = useState<SidebarLabel | null>(null);
  const {
    authStatus,
    user,
    login,
    logout,
    showNotTeacherPopup,
    teacherClassesUrl,
    dismissNotTeacherPopup,
  } = useAuth();
  const leaveGuard = useGameEditorLeaveGuard();
  const tMain = useTranslations("main");

  const handleSidebarSelect = (label: SidebarLabel, options?: { force?: boolean }) => {
    if (
      !options?.force &&
      label !== selectedLabel &&
      leaveGuard?.isEditorActive &&
      leaveGuard.shouldConfirmLeave
    ) {
      setPendingSidebarLabel(label);
      return;
    }

    setSelectedUserGuideDoc(null);
    setSelectedLabel(label);
  };

  const handleSelectUserGuideDoc = (docKey: UserGuideDocKey) => {
    setSelectedUserGuideDoc(docKey);
  };

  const handleConfirmLeaveEditor = () => {
    if (pendingSidebarLabel) {
      setSelectedLabel(pendingSidebarLabel);
      setPendingSidebarLabel(null);
    }
  };

  const handleCancelLeaveEditor = () => {
    setPendingSidebarLabel(null);
  };

  const handleNotTeacherConfirm = () => {
    dismissNotTeacherPopup();
    window.location.href = teacherClassesUrl;
  };

  useEffect(() => {
    const syncLocalStorageUser = () => {
      const raw = localStorage.getItem("user");
      setHasLocalStorageUser(Boolean(raw));
    };

    syncLocalStorageUser();
    window.addEventListener("storage", syncLocalStorageUser);
    window.addEventListener("user-storage-changed", syncLocalStorageUser);

    return () => {
      window.removeEventListener("storage", syncLocalStorageUser);
      window.removeEventListener("user-storage-changed", syncLocalStorageUser);
    };
  }, []);

  useEffect(() => {
    if (authStatus !== "unauthenticated") {
      setShowTeacherLoginScreen(false);
    }
  }, [authStatus]);

  useEffect(() => {
    if (!hasLocalStorageUser) {
      setShowTeacherLoginScreen(false);
    }
  }, [hasLocalStorageUser]);

  useEffect(() => {
    if (authStatus !== "authenticated") {
      setIsMobileSidebarOpen(false);
    }
  }, [authStatus]);

  if (authStatus === "checking") {
    return (
      <div className="fixed inset-0 z-90 flex min-h-screen w-full items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  if (authStatus === "unauthenticated") {
    if (showTeacherLoginScreen) {
      return (
        <>
          <TeacherLogin
            mode="fullscreen"
            status={authStatus}
            user={user}
            onLogin={login}
            onLogout={logout}
            onBackToMainLogin={() => setShowTeacherLoginScreen(false)}
          />
          {showNotTeacherPopup ? (
            <ConfirmPopUp
              title="คุณยังไม่ได้เป็นครูของ pila"
              message="โปรดสมัคร/เข้าร่วมเป็นครูในระบบ PILA ก่อนเข้าใช้งาน"
              confirmText="ตกลง"
              hideCancelButton={true}
              onConfirm={handleNotTeacherConfirm}
              onCancel={dismissNotTeacherPopup}
            />
          ) : null}
        </>
      );
    }

    return (
      <>
        <MainLogin onTeacherLoginClick={() => setShowTeacherLoginScreen(true)} />
        {showNotTeacherPopup ? (
          <ConfirmPopUp
            title="คุณยังไม่ได้เป็นครูของ pila"
            message="โปรดสมัคร/เข้าร่วมเป็นครูในระบบ PILA ก่อนเข้าใช้งาน"
            confirmText="ตกลง"
            hideCancelButton={true}
            onConfirm={handleNotTeacherConfirm}
            onCancel={dismissNotTeacherPopup}
          />
        ) : null}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f3ea] text-neutral-800">
      <Header
        authStatus={authStatus}
        user={user}
        onLogin={login}
        onLogout={logout}
        onToggleSidebar={() => setIsMobileSidebarOpen((prev) => !prev)}
      />
      <div className="flex min-h-[calc(100vh-64px)]">
        <div className="hidden lg:block">
          <Sidebar
            selectedLabel={selectedLabel}
            selectedUserGuideDoc={selectedUserGuideDoc}
            onSelect={handleSidebarSelect}
            onSelectUserGuideDoc={handleSelectUserGuideDoc}
          />
        </div>
        <div
          className={`fixed inset-0 z-80 lg:hidden transition-opacity duration-300 ease-in-out ${
            isMobileSidebarOpen
              ? "pointer-events-auto opacity-100"
              : "pointer-events-none opacity-0"
          }`}
          aria-hidden={!isMobileSidebarOpen}
        >
          <button
            type="button"
            onClick={() => setIsMobileSidebarOpen(false)}
            className={`absolute inset-0 bg-black/45 transition-opacity duration-300 ease-in-out ${
              isMobileSidebarOpen ? "opacity-100" : "opacity-0"
            }`}
            aria-label="Close sidebar"
            tabIndex={isMobileSidebarOpen ? 0 : -1}
          />
          <div
            className={`absolute left-0 top-0 flex h-full w-60 max-w-[85vw] flex-col bg-white shadow-xl transition-transform duration-300 ease-in-out ${
              isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="flex shrink-0 items-center justify-end border-b border-[#ececec] p-2">
              <button
                type="button"
                onClick={() => setIsMobileSidebarOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100"
                aria-label="Close sidebar"
                tabIndex={isMobileSidebarOpen ? 0 : -1}
              >
                <IoClose size={20} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              <Sidebar
                selectedLabel={selectedLabel}
                selectedUserGuideDoc={selectedUserGuideDoc}
                onSelect={handleSidebarSelect}
                onSelectUserGuideDoc={handleSelectUserGuideDoc}
                onCloseMobile={() => setIsMobileSidebarOpen(false)}
              />
            </div>
          </div>
        </div>
        <Main
          selectedLabel={selectedLabel}
          onNavigateToLabel={handleSidebarSelect}
        />
      </div>

      {selectedUserGuideDoc ? (
        <UserGuidePdfViewer
          docKey={selectedUserGuideDoc}
          onClose={() => setSelectedUserGuideDoc(null)}
        />
      ) : null}

      {pendingSidebarLabel ? (
        <ConfirmPopUp
          title={tMain("confirmLeaveEditorTitle")}
          message={tMain("confirmLeaveEditorMessage")}
          confirmText={tMain("confirm")}
          cancelText={tMain("cancel")}
          onConfirm={handleConfirmLeaveEditor}
          onCancel={handleCancelLeaveEditor}
        />
      ) : null}
    </div>
  );
}

export default function Home() {
  return (
    <GameEditorLeaveGuardProvider>
      <HomeContent />
    </GameEditorLeaveGuardProvider>
  );
}
